import React from 'react';
import * as Sentry from '@sentry/react';

/**
 * Catches React rendering errors and shows a friendly fallback instead of a
 * white screen of death. Useful for production where the user might hit an
 * edge case we didn't catch in dev.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console so users can copy-paste it if they file a bug report,
    // and report to Sentry so we hear about it without waiting for a report.
    // Sentry.init in src/index.js short-circuits when no DSN is configured,
    // so this call is a harmless no-op in dev / CI builds without the env var.
    console.error('Etsy Fee Calculator error:', error, errorInfo);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo?.componentStack } },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleClearStorage = () => {
    if (window.confirm('This will clear all your saved products and settings. Continue?')) {
      try {
        Object.keys(localStorage)
          .filter(k => k.startsWith('etsyCalculator') || k.startsWith('etsy-calc'))
          .forEach(k => localStorage.removeItem(k));
        window.location.reload();
      } catch (e) {
        console.error(e);
      }
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#fefdf8',
          padding: '2rem 1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: '32rem',
            width: '100%',
            background: '#ffffff',
            border: '2px solid #1a1a2e',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '4px 6px 0 rgba(26,26,46,0.15)',
          }}
        >
          <h1 style={{ fontSize: '1.65rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.5rem' }}>
            🛠️ Something broke on this page
          </h1>
          <p style={{ fontSize: '1.02rem', color: 'rgba(26,26,46,0.75)', lineHeight: 1.55, marginBottom: '1rem' }}>
            Sorry about that. The calculator hit an unexpected error. Try one of these:
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '0.5rem 1rem',
                background: '#1e3a8a',
                color: '#ffffff',
                border: '2px solid #1a1a2e',
                borderRadius: '0.5rem',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '2px 3px 0 rgba(26,26,46,0.2)',
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.5rem 1rem',
                background: '#fefdf8',
                color: '#1a1a2e',
                border: '2px solid #1a1a2e',
                borderRadius: '0.5rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reload page
            </button>
            <button
              onClick={this.handleClearStorage}
              style={{
                padding: '0.5rem 1rem',
                background: 'transparent',
                color: '#9f1239',
                border: '1.5px dashed #9f1239',
                borderRadius: '0.5rem',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '0.93rem',
              }}
            >
              Clear all saved data
            </button>
          </div>
          {this.state.error && (
            <details style={{ marginTop: '1rem', fontSize: '0.88rem' }}>
              <summary style={{ cursor: 'pointer', color: 'rgba(26,26,46,0.6)' }}>
                Technical details
              </summary>
              <pre
                style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  background: 'rgba(26,26,46,0.05)',
                  border: '1px solid rgba(26,26,46,0.1)',
                  borderRadius: '0.4rem',
                  overflow: 'auto',
                  fontSize: '0.86rem',
                  color: 'rgba(26,26,46,0.7)',
                }}
              >
                {String(this.state.error.message || this.state.error)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
